// Core components
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';
import { MatDialog, MatInput } from '@angular/material';

// Third party components
import { FieldType } from '@ngx-formly/material';
import { TranslateService } from '@ngx-translate/core';
import { Address } from 'ngx-google-places-autocomplete/objects/address';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

// Custom components
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ManualAddressDialogComponent } from 'src/app/shared/components/dialogs/manual-address-dialog/manual-address-dialog.component';
import { CustomAddress } from 'src/app/shared/interfaces/address.interface';
import { HelperService } from 'src/app/shared/services/helper.service';
// import { Options } from 'ngx-google-places-autocomplete/objects/options/options';

// @see https://stackblitz.com/edit/angular-g9txuh-zysdfi?file=src/app/autocomplete-type.component.ts

@Component({
  selector: 'app-formly-google-autocomplete-type',
  template: `
    <input matInput class="d-none"
      [formControl]="formControl">
    <input matInput class="displayed-google-autocomplete"
      ngx-google-places-autocomplete
      #placesRef="ngx-places"
      [formControl]="formControl2"
      [formlyAttributes]="field"
      [placeholder]="to.placeholder"
      (onAddressChange)="handleAddressChange($event)"
      (click)="onClick()">
  `,
})
export class GoogleAutocompleteTypeComponent extends FieldType implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  @ViewChild(MatInput, { static: false }) formFieldControl: MatInput;
  formControl2: AbstractControl = new FormControl();
  /**
  [options]="googleOptions"
  googleOptions: Options = {
    types: [],
    componentRestrictions: {
      country: 'IT',
    },
    fields: [
      'address_components',
      'geometry',
    ],
    bounds: null,
    strictBounds: null,
  };
  **/

  constructor(
    private toastrService: ToastrService,
    private helperService: HelperService,
    private translateService: TranslateService,
    private dialog: MatDialog,
  ) {
    super();
  }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    super.ngOnInit();

    if (this.to.disabled) {
      this.formControl2.disable();
    }

    if (this.formControl.value) {
      this.formControl2.setValue(this.helperService.getFormattedAddress(this.formControl.value));
    }

    this.subscriptions.push(
      this.formControl.valueChanges.subscribe(
        res => {
          if (res) {
            const formattedAddress = this.helperService.getFormattedAddress(res);
            if (formattedAddress) {
              this.formControl2.setValue(formattedAddress);
            } else {
              this.handleInvalidAddressEntry(res);
            }
          }
        }
      )
    );

    // Subscribe to formControl2. This is used to allow
    // user to delete an address
    this.subscriptions.push(
      this.formControl2.valueChanges.subscribe(
        res => {
          if (res === '') {
            this.formControl.setValue(undefined);
          }
        }
      )
    )
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Handle the address change event in a Google Autocomplete field
   * and set the appropriate value to his components
   *
   * @see https://www.npmjs.com/package/ngx-google-places-autocomplete
   *
   * @since 1.0.0
   *
   * @param address the Address obj returned from Google Maps APIs
   * @param controlName The name of the formControl used to get the Address
   */
  handleAddressChange(address: Address): void {
    const formattedAddress = this.getAddressValues(address);
    if (formattedAddress) {
      this.formControl.setValue(formattedAddress);
    }
  }

  /**
   * Create a "CustomAddress" object using values
   * provided from Google Autocomplete result and returns it
   *
   * @since 1.0.0
   */
  getAddressValues(address: Address): CustomAddress | void {
    if (!address.address_components) {
      this.toastrService.error(this.translateService.instant('common.errors.invalid_address'));
      return;
    }
    const addressValues: CustomAddress = {
      route: '',
      streetNumber: '',
      city: '',
      postalCode: '',
      countryCodeTwo: '',
      latLng: '',
    };
    for (const item of address.address_components) {
      if (item.types.includes('route')) {
        addressValues.route = item.long_name;
      }
      if (item.types.includes('street_number')) {
        addressValues.streetNumber = item.long_name;
      }
      if (item.types.includes('locality')) {
        addressValues.city = item.long_name;
      }
      if (item.types.includes('postal_code')) {
        addressValues.postalCode = item.long_name;
      }
      if (item.types.includes('country')) {
        addressValues.countryCodeTwo = item.short_name;
      }
      if (item.types.includes('administrative_area_level_1')) {
        addressValues.administrativeArea1 = item.long_name;
      }
      if (item.types.includes('administrative_area_level_2')) {
        addressValues.administrativeArea2 = item.long_name;
      }
      if (item.types.includes('administrative_area_level_3')) {
        addressValues.administrativeArea3 = item.long_name;
      }
    }
    if (address.geometry && address.geometry.location) {
      const lat = address.geometry.location.lat();
      const lng = address.geometry.location.lng();
      addressValues.latLng = `${lat},${lng}`;
    }
    return addressValues;
  }

  /**
   * Once user select an address from google autocomplete addresses list
   * we check fi that address has all needed data. If not we call this
   * method that ask user to check entered data or to manually insert
   * address data
   *
   * @since 1.0.0
   */
  handleInvalidAddressEntry(googleRes?: any): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.googleAutocomplete.title'),
        message: this.translateService.instant('dialogs.googleAutocomplete.message'),
        btnOkText: this.translateService.instant('dialogs.googleAutocomplete.btnOkText'),
        btnOkIcon: 'fa-reply',
        btnCancelText: this.translateService.instant('dialogs.googleAutocomplete.btnCancelText'),
        btnCancelIcon: 'fa-edit',
        btnCancelColor: 'accent',
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          // Rese values
          this.formControl.setValue('', { emitEvent: false });
          this.formControl2.setValue('', { emitEvent: false });

          // Open a custom popup used to let user manually fill address data
          if (!res2) {
            this.openManualAddressDialog(googleRes)
          }
          dialogRef.close();
        }
      );
  }

  /**
   * Open a new dialog used to let user manually fill address
   * data and save that data
   *
   * @since 1.0.0
   */
  openManualAddressDialog(googleRes?: any): void {
    const addressData: any = {};
    if (googleRes) {
      addressData.route = googleRes.route;
      addressData.streetNumber = googleRes.streetNumber;
      addressData.postalCode = googleRes.postalCode;
      addressData.city = googleRes.city;
      addressData.administrativeArea1 = googleRes.administrativeArea1;
      addressData.administrativeArea2 = googleRes.administrativeArea2;
      addressData.administrativeArea3 = googleRes.administrativeArea3;
      addressData.countryCodeTwo = googleRes.countryCodeTwo;
    }
    // @todo completare
    const dialogRef = this.dialog.open(ManualAddressDialogComponent, {
      data: {
        ...addressData
      },
      width: '600px',
      autoFocus: false,
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          const formattedAddress = this.helperService.getFormattedAddress(res2);
          this.formControl.setValue(res2);
          this.formControl2.setValue(formattedAddress);
        }
      );
  }

  /**
   * Once formControl2 has been clicked just make the label
   * float
   *
   * @todo this cause label to float even once user click in
   * some other field and it's not good. Would be nice to find
   * a way to make label disappear once user defocus the input
   *
   * @since 1.0.0
   */
  onClick(): void {
    this.formField.floatLabel = 'always';
  }
}
